// フィラメント試験データ一覧（CSV参照用）

export interface FilamentTestReference {
  id: string;
  name: string;
  maker?: string;
  color?: string;
  csvPath?: string; // CSV が存在する場合のパス
}

/**
 * 全フィラメントの試験データ参照リスト
 * csvPath が undefined の場合は未測定
 */
export const filamentTestReferences: FilamentTestReference[] = [
  {
    id: 'pla',
    name: 'PLA',
    maker: 'ELEGOO',
    color: 'Black',
    // csvPath は fetchStressStrainData の自動探索に任せる（/bending/csv/ から取得）
  },
  {
    id: 'pla-plus',
    name: 'PLA+',
    maker: 'Generic',
    color: 'Black',
    // csvPath: undefined // 未測定の例
  },
  {
    id: 'pla-cf',
    name: 'PLA-CF',
    maker: 'Generic',
    color: 'Black',
    // csvPath: undefined
  },
  {
    id: 'pla-silk',
    name: 'PLA-Silk',
    maker: 'Generic',
    color: 'Gold',
    // csvPath: undefined
  },
  {
    id: 'matte-pla',
    name: 'Matte PLA',
    maker: 'Generic',
    color: 'White',
    // csvPath: undefined
  },
  {
    id: 'pla-pro',
    name: 'PLA-Pro',
    maker: 'Generic',
    color: 'Black',
    // csvPath: undefined
  },
  {
    id: 'ht-pla',
    name: 'HT-PLA',
    maker: 'Generic',
    color: 'Natural',
    // csvPath: undefined
  },
  {
    id: 'petg',
    name: 'PETG',
    maker: 'Generic',
    color: 'Clear',
    // csvPath は fetchStressStrainData の自動探索に任せる（/bending/csv/ から取得）
  },
  {
    id: 'petg-cf',
    name: 'PETG-CF',
    maker: 'Generic',
    color: 'Black',
    // csvPath: undefined
  },
  {
    id: 'petg-gf',
    name: 'PETG-GF',
    maker: 'Generic',
    color: 'Black',
    // csvPath: undefined
  },
  {
    id: 'abs',
    name: 'ABS',
    maker: 'Generic',
    color: 'Black',
    // csvPath: undefined
  },
  {
    id: 'abs-cf',
    name: 'ABS-CF',
    maker: 'Generic',
    color: 'Black',
    // csvPath: undefined
  },
  {
    id: 'abs-gf',
    name: 'ABS-GF',
    maker: 'Generic',
    color: 'Black',
    // csvPath: undefined
  },
  {
    id: 'asa',
    name: 'ASA',
    maker: 'Generic',
    color: 'Black',
    // csvPath: undefined
  },
  {
    id: 'asa-cf',
    name: 'ASA-CF',
    maker: 'Generic',
    color: 'Black',
    // csvPath: undefined
  },
  {
    id: 'asa-gf',
    name: 'ASA-GF',
    maker: 'Generic',
    color: 'Black',
    // csvPath: undefined
  },
  {
    id: 'pc',
    name: 'PC',
    maker: 'Generic',
    color: 'Clear',
    // csvPath: undefined
  },
  {
    id: 'pc-cf',
    name: 'PC-CF',
    maker: 'Generic',
    color: 'Black',
    // csvPath: undefined
  },
  {
    id: 'pc-gf',
    name: 'PC-GF',
    maker: 'Generic',
    color: 'Black',
    // csvPath: undefined
  },
  {
    id: 'pa',
    name: 'PA（ナイロン）',
    maker: 'Generic',
    color: 'Natural',
    // csvPath: undefined
  },
  {
    id: 'pa-cf',
    name: 'PA-CF',
    maker: 'Generic',
    color: 'Black',
    // csvPath: undefined
  },
  {
    id: 'pa-gf',
    name: 'PA-GF',
    maker: 'Generic',
    color: 'Black',
    // csvPath: undefined
  },
  {
    id: 'tpu',
    name: 'TPU',
    maker: 'Generic',
    color: 'Clear',
    // csvPath: undefined
  }
];

/**
 * フィラメントIDからCSVパスを取得
 */
export function getFilamentCSVPath(filamentId: string): string | undefined {
  const ref = filamentTestReferences.find(f => f.id === filamentId);
  return ref?.csvPath;
}

/**
 * フィラメントIDから名前を取得
 */
export function getFilamentName(filamentId: string): string {
  const ref = filamentTestReferences.find(f => f.id === filamentId);
  return ref?.name || filamentId;
}

/**
 * フィラメントIDからメーカー名を取得
 */
export function getFilamentMaker(filamentId: string): string {
  const ref = filamentTestReferences.find(f => f.id === filamentId);
  return ref?.maker || 'Generic';
}

/**
 * フィラメントIDから色を取得
 */
export function getFilamentColor(filamentId: string): string {
  const ref = filamentTestReferences.find(f => f.id === filamentId);
  return ref?.color || 'Natural';
}

/**
 * フィラメントIDから材料種別を取得（完全一致用）
 * 派生材料はベース材料に正規化せず、そのまま返す
 * 例：pla-cf → PLA-CF, pla-plus → PLA-PLUS, pla → PLA
 */
export function getFilamentType(filamentId: string): string {
  // ID を大文字に変換してトリム
  const idUpper = filamentId.trim().toUpperCase();
  
  // 特殊なケースの変換マップ
  const typeMap: Record<string, string> = {
    'PLA': 'PLA',
    'PLA-PLUS': 'PLA-PLUS',
    'PLA-CF': 'PLA-CF',
    'PLA-SILK': 'PLA-Silk',
    'PLA-PRO': 'PLA-PRO',
    'MATTE-PLA': 'MATTE-PLA',
    'HT-PLA': 'HT-PLA',
    'PETG': 'PETG',
    'PETG-CF': 'PETG-CF',
    'PETG-GF': 'PETG-GF',
    'ABS': 'ABS',
    'ABS-CF': 'ABS-CF',
    'ABS-GF': 'ABS-GF',
    'ASA': 'ASA',
    'ASA-CF': 'ASA-CF',
    'ASA-GF': 'ASA-GF',
    'PC': 'PC',
    'PC-CF': 'PC-CF',
    'PC-GF': 'PC-GF',
    'PA': 'PA',
    'PA-CF': 'PA-CF',
    'PA-GF': 'PA-GF',
    'TPU': 'TPU'
  };
  
  // マップに存在する場合はそのまま返す
  if (typeMap[idUpper]) {
    return typeMap[idUpper];
  }
  
  // マップに無い場合はそのまま返す（変換ルールを削除）
  console.log('[getFilamentType]', { input: filamentId, output: idUpper });
  
  return idUpper;
}

/**
 * フィラメントIDからベース材料種別を抽出
 * 例：pla-cf → PLA, petg-gf → PETG, pla-plus → PLA
 * @deprecated 完全一致が必要な場合は getFilamentType() を使用してください
 */
export function getBaseFilamentType(filamentId: string): string {
  // ID を大文字に変換
  const idUpper = filamentId.toUpperCase();
  
  // 派生材料の接尾辞を除去してベース材料を取得
  let baseType = idUpper
    .replace(/-CF$/, '')
    .replace(/-GF$/, '')
    .replace(/-PLUS$/, '')
    .replace(/-PRO$/, '')
    .replace(/-SILK$/, '')
    .replace(/^MATTE-/, '')
    .replace(/^HT-/, '');
  
  // ハイフンが残っている場合は最初の部分を取得（例：PLA-XXX → PLA）
  if (baseType.includes('-')) {
    baseType = baseType.split('-')[0];
  }
  
  return baseType;
}
